// src/organoid/rightTab/BankingForm.js
import styles from './BankingForm.module.css';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// AddNewBanking 컴포넌트 경로 (프로젝트 구조에 맞게 확인 필요)
import AddNewBanking from '../../components/modal/AddNewBanking';
import { STOCK_TYPES } from '../../components/common/stockType';

function BankingForm({ mode, time, item, onSave, onCancel, onDelete, onRetrieve, isEditMode, patientCode }) {
    const { t } = useTranslation();
    const { tmpKey } = useParams();

    // ────────────────────────────────────────────────────────────────
    // [수정 1] Helper 함수들을 최상단으로 이동 (State 초기화에 쓰이므로)
    // ────────────────────────────────────────────────────────────────
    const hashmarkToSharp = (word) => {
        if (!word) return '';
        return word.replaceAll('hashmark', '#');
    };

    const sharpToHashmark = (word) => {
        if (!word) return '';
        return word.replaceAll('#', 'hashmark');
    };

    // 날짜 파싱
    const parseDateString = (dateString) => {
        if (!dateString) return { year: "", month: "", day: "" };

        // YYYYMMDD
        if (/^\d{8}$/.test(dateString)) {
            return {
                year: dateString.slice(0, 4),
                month: dateString.slice(4, 6),
                day: dateString.slice(6, 8),
            };
        }
        // YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            const [year, month, day] = dateString.split("-");
            return { year, month, day };
        }
        return { year: "", month: "", day: "" };
    };

    // ────────────────────────────────────────────────────────────────
    // [수정 2] 모든 Hook 선언을 조건문(if) 위로 올리기
    // Add 모드일 때 item이 null일 수 있으므로 '?.' 연산자로 안전하게 처리
    // ────────────────────────────────────────────────────────────────
    
    // 초기 상태 설정 (item이 없어도 죽지 않도록 ?. 처리)
    const { year: fY, month: fM, day: fD } = parseDateString(item?.FreezingDate);
    const { year: eY, month: eM, day: eD } = parseDateString(item?.ExporteDate);

    // 초기 데이터 객체
    const initialFormData = {
        ...(item || {}), // item이 없으면 빈 객체
        FreezingDateYear: fY,
        FreezingDateMonth: fM,
        FreezingDateDay: fD,
        ExporteDateYear: eY,
        ExporteDateMonth: eM,
        ExporteDateDay: eD,
    };

    // State 선언 (항상 실행됨)
    const [oldItem] = useState(initialFormData);
    const [formData, setFormData] = useState(initialFormData);
    const [lineList, setLineList] = useState([]);
    const [filteredLineList, setFilteredLineList] = useState([]);
    // [2026-09-09] Master 체크박스(0/1) → MCB/WCB/ARB 3지선다 (문자열 '0'/'1'/'2')
    const [stockValue, setStockValue] = useState(String(item?.Master ?? '0'));
    const [organoidData, setOrganoidData] = useState({});

    // 내부 모달 State
    const [isInternalModalOpen, setIsInternalModalOpen] = useState(false); // 이름 충돌 방지를 위해 변경 권장하나, 여기선 로직 분리로 해결됨
    const [modalType, setModalType] = useState('save'); 
    const [draftOpinion, setDraftOpinion] = useState('');

    // ─── Effects (항상 실행됨) ───

    // 1. Line List
    useEffect(() => {
        fetch("/api/getLineList", {
            method: "post",
            headers: { "content-type": "application/json" }
        })
            .then(response => response.json())
            .then(data => setLineList(data))
            .catch(err => console.error("Line List Fetch Error:", err));
    }, []);

    // 2. Organoid Info
    useEffect(() => {
        if (tmpKey) {
            fetch('/api/getBankingInfoLimitOne', {
                method: 'post',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ param: tmpKey }),
            })
                .then(response => response.json())
                .then(data => setOrganoidData(data))
                .catch(err => console.error("Organoid Info Fetch Error:", err));
        }
    }, [tmpKey]);

    // 3. PatientCode 프리필
    useEffect(() => {
        // isEditMode가 true일 때만 로직이 돌도록 조건 추가 가능
        if (isEditMode && !formData.PatientCode && patientCode) {
            setFormData(prev => ({ ...prev, PatientCode: patientCode }));
        }
    }, [patientCode, formData.PatientCode, isEditMode]);

    // 4. tmpKey 프리필
    useEffect(() => {
        if (isEditMode && !formData.Organoid_Info_Key && tmpKey) {
            setFormData(prev => ({ ...prev, Organoid_Info_Key: tmpKey }));
        }
    }, [tmpKey, formData.Organoid_Info_Key, isEditMode]);

    // 5. LabCode 프리필
    useEffect(() => {
        if (isEditMode && organoidData[0]?.LabCode && !formData.LabCode) {
            setFormData(prev => ({ ...prev, LabCode: organoidData[0].LabCode }));
        }
    }, [organoidData, formData.LabCode, isEditMode]);

    // 6. Vessel 필터링
    useEffect(() => {
        if (["1", "2", "3", "4"].includes(String(formData.Vessel))) {
            setFilteredLineList(
                lineList.filter(lineItem => String(lineItem.vesselNo) === String(formData.Vessel))
            );
        } else {
            setFilteredLineList([]);
        }
    }, [formData.Vessel, lineList]);


    // ────────────────────────────────────────────────────────────────
    // [수정 3] 이제 분기 처리를 수행 (모든 Hook 선언이 끝난 후)
    // ────────────────────────────────────────────────────────────────
    if (!isEditMode) {
        return (
            <AddNewBanking
                patientCode={patientCode}
                tmpKey={tmpKey} 
                onCancel={onCancel}
                parentTime={time}
            />
        );
    }

    // ────────────────────────────────────────────────────────────────
    // [CASE 2] 수정(Edit) 모드 렌더링
    // ────────────────────────────────────────────────────────────────

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "Master") {
            setStockValue(value);
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSaveClick = (e) => { e.preventDefault(); setModalType('save'); setIsInternalModalOpen(true); };
    const handleDeleteClick = (e) => { e.preventDefault(); setModalType('delete'); setIsInternalModalOpen(true); };
    // [2026-10-07] 반출자·반출일이 빈 채로 기안이 올라가 반출 기록이 남지 않았다. 의견 입력 전에 막는다
    const handleExportClick = (e) => {
        e.preventDefault();
        const hasExporter = String(formData.Exporter || '').trim() !== '';
        const hasExportDate = formData.ExporteDateYear && formData.ExporteDateMonth && formData.ExporteDateDay;
        if (!hasExporter || !hasExportDate) {
            alert(t("반출자와 반출일을 입력해 주세요."));
            return;
        }
        setModalType('export');
        setIsInternalModalOpen(true);
    };

    const handleModalSubmit = (e) => {
        e.preventDefault();

        const makeDate = (y, m, d) => {
            if (!y || !m || !d) return '';
            return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
        };

        const freezingDate = makeDate(formData.FreezingDateYear, formData.FreezingDateMonth, formData.FreezingDateDay);
        const exportDate = makeDate(formData.ExporteDateYear, formData.ExporteDateMonth, formData.ExporteDateDay);

        const combinedData = {
            ...formData,
            Organoid_Info_Key: sharpToHashmark(formData.Organoid_Info_Key || ''),
            FreezingDate: freezingDate,
            ExporteDate: exportDate,
            Master: stockValue,
            ...(time && { ParentTime: time }),
            draftOpinion,
        };

        if (modalType === 'save') {
            if (isEditMode) {
                const oldDateF = makeDate(oldItem.FreezingDateYear, oldItem.FreezingDateMonth, oldItem.FreezingDateDay);
                const oldDateE = makeDate(oldItem.ExporteDateYear, oldItem.ExporteDateMonth, oldItem.ExporteDateDay);
                
                // 단순 비교 로직 유지
                const isFormChanged = JSON.stringify(formData) !== JSON.stringify(oldItem) || stockValue !== String(oldItem.Master ?? '0');
                
                if (!isFormChanged) {
                     alert(t("변경사항이 없습니다."));
                     return;
                }
            }
            onSave(combinedData, isEditMode);

        } else if (modalType === 'delete') {
            onDelete({ ...formData, draftOpinion });
        } else if (modalType === 'export') {
            // 연·월·일 칸을 합쳐야 반출일이 저장된다 (예전엔 원래 값 '' 이 그대로 나갔다)
            onRetrieve({ ...formData, Exporter: String(formData.Exporter || '').trim(), ExporteDate: exportDate, draftOpinion });
        }

        setIsInternalModalOpen(false);
        setDraftOpinion('');
    };

    const inputRow = (label, name, node) => (
        <div className={styles.bankingItem} key={name}>
            <label className={styles.bankingLabel}>{label}:</label>
            {node}
        </div>
    );

    // inputs 배열 생성 부분은 렌더링 내부에 있어도 되지만, 
    // 가독성을 위해 기존처럼 return 직전에 정의
    const inputs = [
        inputRow("Patient Code", "PatientCode", (
            <input
                type="text"
                name="PatientCode"
                value={formData.PatientCode || ""}
                onChange={handleChange}
                className={styles.bankingInput}
                readOnly
            />
        )),
        inputRow("Stock", "Master", (
            <select
                name="Master"
                value={stockValue}
                onChange={handleChange}
                className={styles.bankingInput}
            >
                {STOCK_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.full}</option>
                ))}
            </select>
        )),
        inputRow("Organoid Info Key", "Organoid_Info_Key", (
            <input
                type="text"
                name="Organoid_Info_Key_display"
                value={hashmarkToSharp(formData.Organoid_Info_Key || "")}
                onChange={(e) =>
                    setFormData(prev => ({
                        ...prev,
                        Organoid_Info_Key: sharpToHashmark(e.target.value)
                    }))
                }
                className={styles.bankingInput}
                placeholder="예: GBC067001T#1"
                readOnly
            />
        )),
        inputRow("Lab Code", "LabCode", (
            <input
                type="text"
                name="LabCode"
                value={formData.LabCode || ""}
                onChange={handleChange}
                className={styles.bankingInput}
            />
        )),
        inputRow("Storage", "Vessel", (
            <select
                name="Vessel"
                value={formData.Vessel || ""}
                onChange={handleChange}
                className={styles.bankingInput}
            >
                <option value="">Select</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
            </select>
        )),
        inputRow("Line", "Line", (
            <select
                name="Line"
                value={formData.Line || ""}
                onChange={handleChange}
                className={styles.bankingInput}
            >
                <option value="">Select</option>
                {filteredLineList.map(lineItem => (
                    <option key={lineItem.lineName} value={lineItem.lineName}>
                        {lineItem.lineName}
                    </option>
                ))}
            </select>
        )),
        inputRow("Box", "Box", (
            <select
                name="Box"
                value={formData.Box || ""}
                onChange={handleChange}
                className={styles.bankingInput}
            >
                <option value="">Select</option>
                {[...Array(13).keys()].map(i => (
                    <option key={`BOX${i + 1}`} value={`BOX${i + 1}`}>
                        BOX{i + 1}
                    </option>
                ))}
            </select>
        )),
        inputRow("Position", "Position", (
            <input
                type="number"
                name="Position"
                value={formData.Position ?? "0"}
                onChange={handleChange}
                className={styles.bankingInput}
                min="0"
                max="100"
            />
        )),
        inputRow("Passage No.", "PassageNo", (
            <input
                type="text"
                name="PassageNo"
                value={formData.PassageNo || ""}
                onChange={handleChange}
                className={styles.bankingInput}
            />
        )),
        inputRow("Freezing Date", "FreezingDate", (
            <>
                <input type="number" name="FreezingDateYear" value={formData.FreezingDateYear || ""} onChange={handleChange} className={styles.bankingInput} placeholder="YYYY" min="1900" max="2099" />
                <span>-</span>
                <input type="number" name="FreezingDateMonth" value={formData.FreezingDateMonth || ""} onChange={handleChange} className={styles.bankingInput} placeholder="MM" min="1" max="12" />
                <span>-</span>
                <input type="number" name="FreezingDateDay" value={formData.FreezingDateDay || ""} onChange={handleChange} className={styles.bankingInput} placeholder="DD" min="1" max="31" />
            </>
        )),
        inputRow("Keeper", "Keeper", (
            <input
                type="text"
                name="Keeper"
                value={formData.Keeper || ""}
                onChange={handleChange}
                className={styles.bankingInput}
            />
        )),
        inputRow("Freezing Media", "FreezingMedia", (
            <>
                <input list="freezingMediaList" name="FreezingMedia" value={formData.FreezingMedia || ""} onChange={handleChange} className={styles.bankingInput} />
                <datalist id="freezingMediaList">
                    <option value="Recovery Cell Culture Freezing Medium" />
                </datalist>
            </>
        )),
        inputRow("Dome/Vial or Cell Number", "domePerVialOrCellNumber", (
            <input
                type="text"
                name="domePerVialOrCellNumber"
                value={formData.domePerVialOrCellNumber || ""}
                onChange={handleChange}
                className={styles.bankingInput}
            />
        )),
        inputRow("Culture Media", "CultureMedia", (
            <input
                type="text"
                name="CultureMedia"
                value={formData.CultureMedia || ""}
                onChange={handleChange}
                className={styles.bankingInput}
            />
        )),
        inputRow("Mycoplasma", "Mycoplasma", (
            <select
                name="Mycoplasma"
                value={formData.Mycoplasma || ""}
                onChange={handleChange}
                className={styles.bankingInput}
            >
                <option value="">Select</option>
                <option value="Positive">Positive</option>
                <option value="Negative">Negative</option>
                <option value="NA">NA</option>
            </select>
        )),
        inputRow("Thawing No.", "thawingNo", (
            <input
                type="text"
                name="thawingNo"
                value={formData.thawingNo || ""}
                onChange={handleChange}
                className={styles.bankingInput}
            />
        )),
        inputRow(t("비고"), "annotation", (
            <input
                type="text"
                name="annotation"
                value={formData.annotation || ""}
                onChange={handleChange}
                className={styles.bankingInput}
            />
        )),
        inputRow("Exporter", "Exporter", (
            <input
                type="text"
                name="Exporter"
                value={formData.Exporter || ""}
                onChange={handleChange}
                className={styles.bankingInput}
            />
        )),
        inputRow("Export Date", "ExporteDate", (
            <>
                <input type="number" name="ExporteDateYear" value={formData.ExporteDateYear || ""} onChange={handleChange} className={styles.bankingInput} placeholder="YYYY" min="1900" max="2099" />
                <span>-</span>
                <input type="number" name="ExporteDateMonth" value={formData.ExporteDateMonth || ""} onChange={handleChange} className={styles.bankingInput} placeholder="MM" min="1" max="12" />
                <span>-</span>
                <input type="number" name="ExporteDateDay" value={formData.ExporteDateDay || ""} onChange={handleChange} className={styles.bankingInput} placeholder="DD" min="1" max="31" />
            </>
        )),
    ];

    return (
        <form className={styles.formContainer}>
            {inputs}
            <div className={styles.bankingInputButton}>
                {isEditMode && (
                    <button onClick={handleDeleteClick} className={styles.deleteButton}>{t("Delete")}</button>
                )}
                {isEditMode && String(formData.Position) !== "0" && (
                    <button onClick={handleExportClick} className={styles.exportButton}>{t("Export")}</button>
                )}
                <button onClick={handleSaveClick} className={styles.saveButton}>{t("Save")}</button>
                <button onClick={onCancel} className={styles.cancelButton}>{t("Cancel")}</button>
            </div>

            {isInternalModalOpen && (
                <div className={styles.modal} onClick={() => setIsInternalModalOpen(false)}>
                    <div className={styles.modalBackground} onClick={e => e.stopPropagation()}>
                        <h2>
                            {modalType === 'save' && t("기안 의견 입력")}
                            {modalType === 'delete' && t("삭제 기안 의견 입력")}
                            {modalType === 'export' && t("반출 기안 의견 입력")}
                        </h2>
                        <textarea
                            value={draftOpinion}
                            onChange={e => setDraftOpinion(e.target.value)}
                            placeholder={t("의견을 입력하세요.")}
                            className={styles.opinionTextarea}
                        />
                        <div className={styles.modalButtons}>
                            <button onClick={handleModalSubmit} className={styles.modalSubmitButton}>
                                {modalType === 'save' && t("Submit")}
                                {modalType === 'delete' && t("Delete Draft")}
                                {modalType === 'export' && t("Export Draft")}
                            </button>
                            <button onClick={() => setIsInternalModalOpen(false)} className={styles.modalCancelButton}>
                                {t("Cancel")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </form>
    );
}

export default BankingForm;